/**
 * "Start by…" on a brand-new project's Home (design "A calmer project Home", board 3): describe it to Flow's chat,
 * or import a folder or a zip (each top folder with its own package becomes a service, as New project › Import does:
 * utils/projectImport.js), or clone one or more git repositories (`onStartFromGit`: sandbox/StartFromGit.jsx, each a
 * service). Apps, files and changes take its place as soon as there is something to show.
 *
 * `useImportIntoProject(projectId, onDone)` holds the import (the page's own drop uses it too): `{ state, importFrom
 * (collected) }`, state `{ phase: 'reading' | 'sending' | 'done' | 'failed', text }` or null. `taken` (the Apps "+",
 * into a project that has services): their short names; an import that would write over one of them is refused.
 */
import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { collectFromFileList } from '../../utils/treeUpload/collectEntries';
import { ARCHIVE_ACCEPT } from '../../utils/treeUpload/readArchive';
import { collectSource, importIntoProject, prepareImport } from '../../utils/projectImport';

const SUGGESTIONS = [
  'A page where customers pick a shop and see today’s opening hours',
  'An API that returns opening hours',
  'Read my spreadsheet and make a dashboard',
];

export function useImportIntoProject(projectId, onDone, { taken = [] } = {}) {
  const [state, setState] = useState(null);
  const busy = state?.phase === 'reading' || state?.phase === 'sending';
  const importFrom = useCallback(async(collected) => {
    if (!projectId || !collected?.files?.length) {
      setState({ phase: 'failed', text: 'Nothing to import: the folder is empty or could not be read.' });
      return;
    }
    setState({ phase: 'reading', text: 'Reading what you picked…' });
    try {
      const prepared = await prepareImport(await collectSource(collected));
      const { plan } = prepared;
      if (plan.problems.length > 0 || plan.files.length === 0) {
        setState({ phase: 'failed', text: plan.problems[0] || 'Nothing in it can be imported.' });
        return;
      }
      const clash = (prepared.layout?.services || []).find((service) => taken.includes(service.name));
      if (clash) {
        setState({ phase: 'failed', text: `services/${clash.name} already exists: rename the folder (or the zip) and try again.` });
        return;
      }
      setState({ phase: 'sending', text: `Sending ${plan.files.length} file${plan.files.length === 1 ? '' : 's'}…` });
      const result = await importIntoProject({ projectId, prepared });
      if (result.failed || result.cancelled) {
        setState({ phase: 'failed', text: `${result.failed || 'Some'} file${result.failed === 1 ? '' : 's'} could not be sent. Try again.` });
      } else {
        const count = result.services.length;
        setState({ phase: 'done', text: `Imported ${count} service${count === 1 ? '' : 's'}.` });
      }
      onDone?.(result);
    } catch (error) {
      setState({ phase: 'failed', text: error?.message || 'This could not be imported.' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `taken` is compared by value
  }, [projectId, onDone, taken.join('\n')]);
  return { state, busy, importFrom };
}

export default function StartBy({ chatOn = true, onStartInChat, importState = null, importBusy = false, onImport, onStartFromGit = null }) {
  const [text, setText] = useState('');
  const folderRef = useRef(null);
  const archiveRef = useRef(null);
  const picked = (event) => {
    const collected = collectFromFileList(event.target.files);
    event.target.value = '';
    onImport(collected);
  };
  const start = (event) => {
    event.preventDefault();
    if (text.trim()) { onStartInChat(text.trim()); }
  };
  return (
    <section aria-labelledby="ph-start" className="ph-card ph-start" data-testid="home-start-by">
      <div className="ph-start-intro">
        <h2 id="ph-start">Start by…</h2>
        <p className="ph-muted">Tell Flow what to build, or bring the code you already have. Apps, files and changes appear on this page as soon as there is something to show.</p>
      </div>
      <div className="ph-start-ways">
        {chatOn && (
          <form className="ph-start-way" onSubmit={start}>
            <label className="ph-start-label" htmlFor="ph-start-ask">…describing it</label>
            <div className="ph-start-box">
              <textarea
                id="ph-start-ask"
                onChange={(event) => setText(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { start(event); } }}
                placeholder={SUGGESTIONS[0]}
                rows={3}
                value={text}
              />
              <div className="ph-start-box-foot">
                <button className="ph-btn ph-btn-primary" disabled={!text.trim()} type="submit">Start in chat</button>
              </div>
            </div>
            <div className="ph-start-suggestions">
              {SUGGESTIONS.map((suggestion) => (
                <button className="ph-suggestion" key={suggestion} onClick={() => setText(suggestion)} type="button">{suggestion}</button>
              ))}
            </div>
          </form>
        )}
        <div className="ph-start-way">
          <span className="ph-start-label">…importing a folder or a zip</span>
          <div className="ph-start-drop">
            <span>Drop a folder or a .zip anywhere on this page</span>
            <div className="ph-start-drop-actions">
              <button className="ph-btn" disabled={importBusy} onClick={() => folderRef.current?.click()} type="button">Choose folder</button>
              <button className="ph-btn" disabled={importBusy} onClick={() => archiveRef.current?.click()} type="button">Choose .zip</button>
            </div>
            <input data-testid="start-by-folder" hidden multiple onChange={picked} ref={folderRef} type="file" webkitdirectory="" />
            <input accept={ARCHIVE_ACCEPT} data-testid="start-by-zip" hidden onChange={picked} ref={archiveRef} type="file" />
          </div>
          {importState
            ? <span className={importState.phase === 'failed' ? 'ph-error' : 'ph-muted ph-small'} role={importState.phase === 'failed' ? 'alert' : 'status'}>{importState.text}</span>
            : <span className="ph-muted ph-small">Each top folder with its own package becomes a service.</span>}
        </div>
        {onStartFromGit && (
          <div className="ph-start-way">
            <span className="ph-start-label">…cloning a git repository</span>
            <div className="ph-start-drop">
              <span>One or more repositories, public or private (with your connected accounts), from any git server</span>
              <div className="ph-start-drop-actions">
                <button className="ph-btn" disabled={importBusy} onClick={onStartFromGit} type="button">Start from a repository</button>
              </div>
            </div>
            <span className="ph-muted ph-small">Each repository becomes a service, with its remote kept for pull and push.</span>
          </div>
        )}
      </div>
    </section>
  );
}

StartBy.propTypes = {
  chatOn: PropTypes.bool,
  importBusy: PropTypes.bool,
  importState: PropTypes.object,
  onImport: PropTypes.func.isRequired,
  onStartFromGit: PropTypes.func,
  onStartInChat: PropTypes.func.isRequired,
};
